/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Location } from '../models/Location';
import type { LocationRequest } from '../models/LocationRequest';
import type { PaginatedLocationList } from '../models/PaginatedLocationList';
import type { PatchedLocationRequest } from '../models/PatchedLocationRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class LocationsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * API endpoint to view and edit company locations
     * @returns PaginatedLocationList
     * @throws ApiError
     */
    public companiesLocationsList({
        parentCompany,
        limit,
        offset,
    }: {
        parentCompany: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedLocationList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/locations/',
            path: {
                'parent_company': parentCompany,
            },
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * API endpoint to view and edit company locations
     * @returns Location
     * @throws ApiError
     */
    public companiesLocationsCreate({
        parentCompany,
        requestBody,
    }: {
        parentCompany: string,
        requestBody: LocationRequest,
    }): CancelablePromise<Location> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/companies/{parent_company}/locations/',
            path: {
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit company locations
     * @returns Location
     * @throws ApiError
     */
    public companiesLocationsRetrieve({
        id,
        parentCompany,
    }: {
        /**
         * A UUID string identifying this location.
         */
        id: string,
        parentCompany: string,
    }): CancelablePromise<Location> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/locations/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * API endpoint to view and edit company locations
     * @returns Location
     * @throws ApiError
     */
    public companiesLocationsUpdate({
        id,
        parentCompany,
        requestBody,
    }: {
        /**
         * A UUID string identifying this location.
         */
        id: string,
        parentCompany: string,
        requestBody: LocationRequest,
    }): CancelablePromise<Location> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{parent_company}/locations/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit company locations
     * @returns Location
     * @throws ApiError
     */
    public companiesLocationsPartialUpdate({
        id,
        parentCompany,
        requestBody,
    }: {
        /**
         * A UUID string identifying this location.
         */
        id: string,
        parentCompany: string,
        requestBody?: PatchedLocationRequest,
    }): CancelablePromise<Location> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{parent_company}/locations/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit company locations
     * @returns void
     * @throws ApiError
     */
    public companiesLocationsDestroy({
        id,
        parentCompany,
    }: {
        /**
         * A UUID string identifying this location.
         */
        id: string,
        parentCompany: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/companies/{parent_company}/locations/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
        });
    }

}
